import find from 'lodash/find';
import get from 'lodash/get';
import map from 'lodash/map';
import omit from 'lodash/omit';

import predefinedBlocks from './predefined_blocks.js';

import {
  getZfImageForAsset
} from 'zoomforth_media_library/lib/assets.js';

import {
  ZF_JSON_IMAGE_CROP_DATA_TYPE,
  ZF_JSON_OBJECT_TYPE_PROPERTY,
  ZF_JSON_OBJECT_VERSION_PROPERTY
} from 'const_shared.js';

import {
  BLOCK_TYPE_CONTAINER,
  BLOCK_TYPE_CONTENT,
  BLOCK_SUBTYPE_GRID_HERO_LEFT_ALIGNED,
  BLOCK_SUBTYPE_GRID_HERO_MIDDLE_ALIGNED,
  BLOCK_SUBTYPE_GRID,
  BLOCK_SUBTYPE_IMAGE,
  BLOCK_SUBTYPE_SUBPAGE,
  BLOCK_SUBTYPE_GRID_BIO_ROUNDED,
  BLOCK_SUBTYPE_GRID_BIO_ROUNDED_SINGLE,
  BLOCK_SUBTYPE_GRID_BIO_STANDARD,
  BLOCK_SUBTYPE_GRID_BIO_STANDARD_SINGLE,
  BLOCK_SUBTYPE_GRID_IMAGE_AND_TEXT_FULL_BLEED,
  BLOCK_SUBTYPE_GRID_IMAGE_AND_TEXT_BORDERED,
  BLOCK_SUBTYPE_GRID_VIDEO_AND_TEXT_FULL_BLEED,
  BLOCK_SUBTYPE_GRID_VIDEO_AND_TEXT_BORDERED
} from 'const_shared/blocks/block_definitions.js';

import {
  CONTENT_TYPE_PHOTO,
  CONTENT_TYPE_TEXT,

  SECTION_TYPE_HTML_PACKAGE,
  SECTION_TYPE_IFRAME_EMBED,
  SECTION_TYPE_PDF,
  SECTION_TYPE_PRESENTATION,
  SECTION_TYPE_SLIDESHOW,
  SECTION_TYPE_TEXT,
  SECTION_TYPE_TILES,
  SECTION_TYPE_VIDEOPLAYLIST,
  SECTION_TYPE_JOB_FEED,
  SECTION_TYPE_ACCORDION,
  SECTION_TYPE_TABS
} from 'zoomforth_codesbane_viewer/const/sections.js';


const BLOCK_SUBTYPE_TO_STANDARD_TYPE_MAP = Object.freeze({
  [BLOCK_SUBTYPE_IMAGE]: CONTENT_TYPE_PHOTO,
  [BLOCK_SUBTYPE_GRID_HERO_LEFT_ALIGNED]: SECTION_TYPE_TILES,
  [BLOCK_SUBTYPE_GRID_HERO_MIDDLE_ALIGNED]: SECTION_TYPE_TILES,
  [BLOCK_SUBTYPE_GRID]: SECTION_TYPE_TILES,
  [BLOCK_SUBTYPE_GRID_BIO_STANDARD]: SECTION_TYPE_TILES,
  [BLOCK_SUBTYPE_GRID_BIO_ROUNDED]: SECTION_TYPE_TILES,
  [BLOCK_SUBTYPE_GRID_BIO_STANDARD_SINGLE]: SECTION_TYPE_TILES,
  [BLOCK_SUBTYPE_GRID_BIO_ROUNDED_SINGLE]: SECTION_TYPE_TILES,
  [BLOCK_SUBTYPE_GRID_IMAGE_AND_TEXT_FULL_BLEED] :SECTION_TYPE_TILES,
  [BLOCK_SUBTYPE_GRID_IMAGE_AND_TEXT_BORDERED] :SECTION_TYPE_TILES,
  [BLOCK_SUBTYPE_GRID_VIDEO_AND_TEXT_FULL_BLEED] :SECTION_TYPE_TILES,
  [BLOCK_SUBTYPE_GRID_VIDEO_AND_TEXT_BORDERED] :SECTION_TYPE_TILES,
  [CONTENT_TYPE_TEXT]: CONTENT_TYPE_TEXT,
  [SECTION_TYPE_HTML_PACKAGE]: SECTION_TYPE_HTML_PACKAGE,
  [SECTION_TYPE_IFRAME_EMBED]: SECTION_TYPE_IFRAME_EMBED,
  [SECTION_TYPE_PDF]: SECTION_TYPE_PDF,
  [SECTION_TYPE_PRESENTATION]: SECTION_TYPE_PRESENTATION,
  [SECTION_TYPE_SLIDESHOW]: SECTION_TYPE_SLIDESHOW,
  [SECTION_TYPE_TEXT]: SECTION_TYPE_TEXT,
  [SECTION_TYPE_TILES]: SECTION_TYPE_TILES,
  [SECTION_TYPE_VIDEOPLAYLIST]: SECTION_TYPE_VIDEOPLAYLIST,
  [SECTION_TYPE_JOB_FEED]: SECTION_TYPE_JOB_FEED,
  [SECTION_TYPE_ACCORDION]: SECTION_TYPE_ACCORDION,
  [SECTION_TYPE_TABS]: SECTION_TYPE_TABS
});

const transformBlockSubtypeToStandardType = (blockSubtype) =>
  // All of these need to mapped out.
  BLOCK_SUBTYPE_TO_STANDARD_TYPE_MAP[`${blockSubtype}`];


export function transformBlockContentDataToLegacyData(blockContentData) {
  const {
    size,
    position,
    tile_caption
  } = blockContentData;

  return ({
      ...blockContentData,
      renderedTileCaption: tile_caption,
      sizex: size.w,
      sizey: size.h,
      row: position.y,
      col: position.x
  });
};

export function transformBlockContainerDataToLegacyData(blockContainerData) {
  const { blockSubtype } = blockContainerData;

  const transformedSubtype = transformBlockSubtypeToStandardType(blockSubtype);

  return ({
    ...blockContainerData,
    type: transformedSubtype,
    content_type: transformedSubtype
  });
};

export function transformBlockDataToLegacyData(blockData) {
  const { blockType } = blockData;

  switch (blockType) {
    case BLOCK_TYPE_CONTAINER:
      return transformBlockContainerDataToLegacyData(blockData);

    case BLOCK_TYPE_CONTENT:
      return transformBlockContentDataToLegacyData(blockData);

    default:
      return blockData;
  }
};

export function extractRelevantImageDataForImageCreation(
  blockId,
  asset
) {
  /*
    This may seem like a bizarre and arbitrary transformer,
    and perhaps there's a better way to articulate this,

    but what we're saying is essentially this:

    We need seed data for image tile creation. This can include
    predefined crop data (which you will find in the parallel
    blocks definition files) which will be used to automatically
    invoke the cropper with, say, a `broderRadius` property.

    This fits with a streamlined UX experience, but also allows
    us to create specialized image tile creators that can contain
    any number of configuration data. Obviously that scope is
    limited to images, but the implication is that we design a
    transformer for every tile creator that parses the essential
    tile creator data and "passes it through" to the content item
    as seed data.
  */

  const blockData = getBlockById(blockId);
  const { tile_inner_zf_image } = blockData;
  const cropData = get(tile_inner_zf_image, 'cropData', {});
  const { originalImage } = asset;
  const zfImageFromAsset = getZfImageForAsset(asset);

  const generatedImageData = (
    omit(
      {
        ...blockData,
        tile_inner_zf_image: {
          ...tile_inner_zf_image,
          ...zfImageFromAsset,

          cropData: {
            ...cropData,
            originalImageSrc: originalImage || zfImageFromAsset.src,
            [ZF_JSON_OBJECT_TYPE_PROPERTY]: ZF_JSON_IMAGE_CROP_DATA_TYPE,
            [ZF_JSON_OBJECT_VERSION_PROPERTY]: 1
          },
        },

        /*
          We need this to be true in the step because the
          dispatch actions expect to receive tile creator
          seed data until this becomes a content item, then
          This will no longer be true because we have
          completed the "handoff" of that data.
        */
        isTileCreator: true
      },

      /*
        We no longer need this because we are passing out
        of the tile creator, and only the tile creator
        cares about this data.
      */
      [
        'blockId',
        'innerTileStyle',
        'size',
        'position'
      ]
    )
  );

  return generatedImageData;
};

export function getChildBlocksForParent(block) {
  const { blockType, blockSubtype } = block;
  var siblings = [];

  if (
       blockType === BLOCK_TYPE_CONTAINER
    && blockSubtype !== BLOCK_SUBTYPE_SUBPAGE
  ) {
    siblings = map(block.childIds, (childId) => (
      getBlockById(childId)
    ));
  }

  return siblings;
};

export function getBlockById(blockId) {
  const foundBlock = find(predefinedBlocks, ['blockId', blockId]);
  return foundBlock;
};